import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraCard, SpecraCardContent, SpecraFormField, SpecraHeading, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useResolveAction } from '../../lib/access/useResolveAction';
import { useAdoptRfiFeedback, useRecordRfiFeedback, useRfiFeedbackHistory, useRfiFeedbackTargets } from './rfiFeedbackHooks';
import { rfiAdoptionLink } from './rfiFeedbackLinks';
const T = Testids.rfiFeedback;
export function RfiFeedbackPanel({ rfiResponseProjectId, zouProjectId }) {
    const history = useRfiFeedbackHistory(rfiResponseProjectId);
    const targets = useRfiFeedbackTargets(rfiResponseProjectId);
    const record = useRecordRfiFeedback();
    const adopt = useAdoptRfiFeedback();
    const access = useResolveAction({
        kind: 'rfi_response',
        id: rfiResponseProjectId,
        objectType: 'delivery.programs',
        objectId: zouProjectId
    }, 'update');
    const location = useLocation();
    const [submissionId, setSubmission] = useState('');
    const [priorVersionId, setPrior] = useState('');
    const [body, setBody] = useState('');
    const [sourceVersionId, setSource] = useState('');
    const [captureId, setCapture] = useState('');
    // A reviewed target is a snapshot: refetch must not silently advance its expected version.
    const [target, setTarget] = useState();
    const [proposedText, setProposed] = useState('');
    const [status, setStatus] = useState('');
    const lock = useRef(false);
    const requests = useRef(new Map());
    const requestId = (operation, intent)=>{
        const key = JSON.stringify([
            operation,
            intent
        ]);
        let id = requests.current.get(key);
        if (!id) {
            id = crypto.randomUUID();
            requests.current.set(key, id);
        }
        return id;
    };
    const busy = record.isPending || adopt.isPending;
    const h = history.isError ? undefined : history.data;
    const versions = h?.versions ?? [];
    const submissions = h?.submissions ?? [];
    const availableTargets = targets.isError ? [] : targets.data ?? [];
    const captures = new Map(availableTargets.map((t)=>[
            t.captureProjectId,
            t.captureName
        ]));
    const targetPresent = !!target && availableTargets.some((t)=>t.captureProjectId === target.captureProjectId && t.kind === target.kind && t.id === target.id);
    useEffect(()=>{
        const pin = new URLSearchParams(location.search).get('feedbackVersion');
        if (!pin || !h?.versions.some((v)=>v.id === pin)) return;
        setSource(pin);
        const row = document.getElementById(`rfi-feedback-${pin}`);
        row?.focus({
            preventScroll: true
        });
        row?.scrollIntoView({
            block: 'center'
        });
    }, [
        location.search,
        h
    ]);
    const run = async (label, action)=>{
        if (lock.current) return;
        lock.current = true;
        setStatus(label);
        try {
            await action();
        } catch (error) {
            setStatus(`${ConnectError.from(error).rawMessage}. Values retained; retry uses the same request. For a stale target, select it again to review its current version.`);
        } finally{
            lock.current = false;
        }
    };
    return /*#__PURE__*/ _jsx(SpecraCard, {
        "data-testid": T.panel,
        "aria-busy": busy || history.isFetching,
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    level: 3,
                    size: "md",
                    children: "Government feedback"
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    children: "Feedback and adoption preserve the actual submitted position. Adoption changes only one reviewed Pursuit field."
                }),
                /*#__PURE__*/ _jsx("output", {
                    "data-testid": T.status,
                    "aria-live": "polite",
                    children: /*#__PURE__*/ _jsx(SpecraText, {
                        "data-testid": busy || history.isLoading ? Testids.pageState.loading : undefined,
                        children: status || (history.isLoading ? 'Loading feedback…' : '')
                    })
                }),
                (history.isError || targets.isError) && /*#__PURE__*/ _jsx(SpecraText, {
                    role: "alert",
                    children: "Unable to load feedback or authorized targets."
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    disabled: busy,
                    onClick: ()=>{
                        void history.refetch();
                        void targets.refetch();
                    },
                    children: "Refresh feedback and targets"
                }),
                h && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        access.allowed && /*#__PURE__*/ _jsxs("fieldset", {
                            disabled: busy,
                            className: "space-y-3",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Actual submission",
                                    htmlFor: "feedback-submission",
                                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                        id: "feedback-submission",
                                        "data-testid": T.submissionSelect,
                                        value: submissionId,
                                        onChange: (e)=>{
                                            setSubmission(e.target.value);
                                            setPrior('');
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "",
                                                children: "Select actual submission…"
                                            }),
                                            submissions.map((s)=>/*#__PURE__*/ _jsxs("option", {
                                                    value: s.id,
                                                    children: [
                                                        s.id,
                                                        " · ",
                                                        s.submittedAt?.toDate().toLocaleString(),
                                                        " · ",
                                                        s.recipient
                                                    ]
                                                }, s.id))
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Explicit predecessor",
                                    htmlFor: "feedback-prior",
                                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                        id: "feedback-prior",
                                        "data-testid": T.priorVersionSelect,
                                        value: priorVersionId,
                                        onChange: (e)=>setPrior(e.target.value),
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "",
                                                children: "First feedback version"
                                            }),
                                            versions.filter((v)=>v.submissionId === submissionId).map((v)=>/*#__PURE__*/ _jsxs("option", {
                                                    value: v.id,
                                                    children: [
                                                        "Feedback v",
                                                        v.version
                                                    ]
                                                }, v.id))
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Government feedback text",
                                    htmlFor: "feedback-body",
                                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                        id: "feedback-body",
                                        "data-testid": T.body,
                                        value: body,
                                        onChange: (e)=>setBody(e.target.value)
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": T.record,
                                    disabled: busy || !body.trim() || !submissions.some((s)=>s.id === submissionId),
                                    onClick: ()=>void run('Recording feedback…', async ()=>{
                                            const intent = {
                                                rfiResponseProjectId,
                                                submissionId,
                                                priorVersionId,
                                                body
                                            };
                                            const result = await record.mutateAsync({
                                                ...intent,
                                                requestId: requestId('record', intent)
                                            });
                                            setBody('');
                                            setPrior(result.version?.id ?? '');
                                            setStatus('Feedback recorded.');
                                        }),
                                    children: "Record feedback version"
                                })
                            ]
                        }),
                        versions.map((v)=>/*#__PURE__*/ _jsxs("section", {
                                id: `rfi-feedback-${v.id}`,
                                tabIndex: -1,
                                "data-testid": T.version,
                                "data-feedback-version-id": v.id,
                                children: [
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        as: "p",
                                        children: [
                                            "Feedback v",
                                            v.version,
                                            " · ",
                                            v.actorUserId,
                                            " · ",
                                            v.createdAt?.toDate().toLocaleString(),
                                            ' ',
                                            "· Submission ",
                                            v.submissionId
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        children: v.body
                                    })
                                ]
                            }, v.id)),
                        availableTargets.length > 0 && /*#__PURE__*/ _jsxs("fieldset", {
                            disabled: busy,
                            className: "space-y-3",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Exact feedback version to adopt",
                                    htmlFor: "feedback-source",
                                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                        id: "feedback-source",
                                        "data-testid": T.sourceVersionSelect,
                                        value: sourceVersionId,
                                        onChange: (e)=>{
                                            setSource(e.target.value);
                                            setProposed(versions.find((v)=>v.id === e.target.value)?.body ?? '');
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "",
                                                children: "Select feedback version…"
                                            }),
                                            versions.map((v)=>/*#__PURE__*/ _jsxs("option", {
                                                    value: v.id,
                                                    children: [
                                                        "Feedback v",
                                                        v.version,
                                                        " · ",
                                                        v.id
                                                    ]
                                                }, v.id))
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "One linked Pursuit",
                                    htmlFor: "feedback-capture",
                                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                        id: "feedback-capture",
                                        "data-testid": T.captureSelect,
                                        value: captureId,
                                        onChange: (e)=>{
                                            setCapture(e.target.value);
                                            setTarget(undefined);
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "",
                                                children: "Select Pursuit…"
                                            }),
                                            Array.from(captures, ([id, name])=>/*#__PURE__*/ _jsx("option", {
                                                    value: id,
                                                    children: name
                                                }, id))
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Canonical target",
                                    htmlFor: "feedback-target",
                                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                        id: "feedback-target",
                                        "data-testid": T.targetSelect,
                                        value: target ? `${target.kind}:${target.id}` : '',
                                        onChange: (e)=>setTarget(availableTargets.find((t)=>t.captureProjectId === captureId && `${t.kind}:${t.id}` === e.target.value)),
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "",
                                                children: "Select target…"
                                            }),
                                            availableTargets.filter((t)=>t.captureProjectId === captureId).map((t)=>/*#__PURE__*/ _jsxs("option", {
                                                    value: `${t.kind}:${t.id}`,
                                                    children: [
                                                        t.kind,
                                                        " · ",
                                                        t.label
                                                    ]
                                                }, `${t.kind}:${t.id}`))
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Only this field",
                                    htmlFor: "feedback-field",
                                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                        id: "feedback-field",
                                        "data-testid": T.fieldSelect,
                                        value: target?.field ?? '',
                                        onChange: (e)=>{
                                            if (!e.target.value) setTarget(undefined);
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "",
                                                children: "Select target first"
                                            }),
                                            target && /*#__PURE__*/ _jsx("option", {
                                                value: target.field,
                                                children: target.field
                                            })
                                        ]
                                    })
                                }),
                                target && /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "p",
                                    children: [
                                        "Reviewed version ",
                                        target.expectedVersionId,
                                        " · Current value:",
                                        ' ',
                                        target.currentText
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Reviewed replacement",
                                    htmlFor: "feedback-replacement",
                                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                        id: "feedback-replacement",
                                        "data-testid": T.proposedText,
                                        value: proposedText,
                                        onChange: (e)=>setProposed(e.target.value)
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": T.adopt,
                                    disabled: busy || !targetPresent || !versions.some((v)=>v.id === sourceVersionId) || !proposedText.trim(),
                                    onClick: ()=>void run('Adopting feedback…', async ()=>{
                                            if (!target) return;
                                            const intent = {
                                                rfiResponseProjectId,
                                                sourceVersionId,
                                                captureProjectId: target.captureProjectId,
                                                targetKind: target.kind,
                                                targetId: target.id,
                                                targetField: target.field,
                                                expectedTargetVersionId: target.expectedVersionId,
                                                proposedText
                                            };
                                            await adopt.mutateAsync({
                                                ...intent,
                                                requestId: requestId('adopt', intent)
                                            });
                                            setTarget(undefined);
                                            setStatus('Feedback adopted into the selected target.');
                                        }),
                                    children: "Adopt reviewed feedback"
                                })
                            ]
                        }),
                        h.adoptions.map((a)=>/*#__PURE__*/ _jsxs("section", {
                                "data-testid": T.adoption,
                                "data-source-version-id": a.sourceVersionId,
                                "data-target-id": a.targetId,
                                children: [
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        as: "p",
                                        children: [
                                            a.actorUserId,
                                            " · ",
                                            a.adoptedAt?.toDate().toLocaleString(),
                                            " · ",
                                            a.beforeVersionId,
                                            " →",
                                            ' ',
                                            a.resultVersionId
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(Link, {
                                        to: rfiAdoptionLink(a),
                                        children: [
                                            "Open adopted ",
                                            a.targetKind,
                                            " · ",
                                            a.targetField
                                        ]
                                    })
                                ]
                            }, a.id))
                    ]
                })
            ]
        })
    });
}
